// Admin view of who has registered for a league (Phase 1b).
//
// Reached from the Leagues table. Teams are Phase 1c, so teamId is shown but
// never set here.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, getDoc, updateDoc, deleteDoc,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import {
  Box, Container, Typography, Chip, CircularProgress, Alert,
  Table, TableContainer, TableBody, TableCell, TableHead, TableRow, Paper,
  IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import PaidIcon from '@mui/icons-material/Paid';
import LinkIcon from '@mui/icons-material/Link';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SmsIcon from '@mui/icons-material/Sms';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyText } from '../utils/shareLink';
import EventSmsDialog from '../components/EventSmsDialog';
import { googleContactsCsv } from '../utils/googleContactsCsv';
import { ageOn } from '../utils/juniors';

const PAY_COLOR = { paid: 'success', pending: 'warning', not_charged: 'default', refunded: 'error' };

export default function LeagueRegistrations() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const emptyWaitForm = { firstName: '', lastName: '', email: '', phone: '', notes: '' };
  const [waitOpen, setWaitOpen] = useState(false);
  const [waitForm, setWaitForm] = useState(emptyWaitForm);
  const [addingWait, setAddingWait] = useState(false);
  const [league, setLeague] = useState(null);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'leagues', leagueId))
      .then(s => { if (s.exists()) setLeague({ id: s.id, ...s.data() }); })
      .catch(e => setError('Could not load the league: ' + e.message));
  }, [leagueId]);

  useEffect(() => {
    // Single-field where; sorted client-side so no composite index is needed.
    const q = query(collection(db, 'leagueRegistrations'), where('leagueId', '==', leagueId));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const at = a.createdAt?.seconds || 0, bt = b.createdAt?.seconds || 0;
        return at - bt;
      });
      setRegs(rows);
      setLoading(false);
    }, (e) => { setError('Could not load registrations: ' + e.message); setLoading(false); });
    return unsub;
  }, [leagueId]);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3000); };
  const [smsOpen, setSmsOpen] = useState(false);

  // Accepting for someone who is not going to click a button — the person who
  // asked at the desk, or a junior whose parent said yes over the phone.
  // Goes through manageLeagueTeam, which re-checks the roster still has room.
  const acceptFor = async (r) => {
    if (!window.confirm(`Put ${r.firstName || 'this player'} on ${r.pendingTeamName || 'that team'}?`)) return;
    try {
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      const { data } = await fn({ teamId: r.pendingTeamId, action: 'acceptInvite', registrationId: r.id });
      flash(`${r.firstName || 'Player'} joined ${data.joined || r.pendingTeamName}.`);
    } catch (e) {
      setError(e.message || 'Could not accept that invitation.');
    }
  };

  // ── Texting everyone on the list ───────────────────────────────────────────
  //
  // An sms: link, not a send from the club's Twilio number: this opens the
  // admin's own messaging app with the recipients filled in, so the reply comes
  // back to them and nothing is billed or logged as a club alert.
  //
  // Wait-listed players are included — they are on the list, and "the court is
  // yours after all" is exactly the message this is for. Cancelled rows are not.
  // Numbers are deduped: siblings and doubles partners share a phone, and a
  // parent texted twice notices.
  const smsRecipients = [...new Set(
    regs.filter(r => r.status !== 'cancelled')
      .map(r => {
        const digits = String(r.phone || '').replace(/\D/g, '');
        if (digits.length === 10) return `+1${digits}`;
        if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
        return '';   // anything else is not a number this can dial
      })
      .filter(Boolean)
  )];
  // Comma-separated after the scheme is the form both iOS and Android accept.
  const smsHref = `sms:${smsRecipients.join(',')}`;
  const copyNumbers = async () => {
    const ok = await copyText(smsRecipients.join(', '));
    if (ok) flash(`${smsRecipients.length} number(s) copied.`);
    else setError('Could not copy the numbers.');
  };

  // ── Exports ────────────────────────────────────────────────────────────────
  const cell = (v) => {
    const t = v == null ? '' : String(v);
    return /[",\r\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
  };
  const download = (name, text) => {
    // BOM so Excel opens it as UTF-8 rather than mangling accented names.
    const blob = new Blob(['\ufeff' + text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };
  const fileStem = () => (league?.name || 'league').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '');

  // Everything on the row, for the club's own records.
  const exportRegistrations = () => {
    const cols = ['firstName', 'lastName', 'email', 'phone', 'smsConsent', 'teamName', 'status',
      'paymentStatus', 'amountPaid', 'priceCents', 'freeAgent', 'isCaptain',
      'guardianName', 'birthdate', 'waitlistPosition', 'notes', 'registered'];
    const rows = regs.map(r => ({
      ...r,
      amountPaid: ((Number(r.amountPaid) || 0) / 100).toFixed(2),
      priceCents: ((Number(r.priceCents) || 0) / 100).toFixed(2),
      registered: r.createdAt?.seconds
        ? new Date(r.createdAt.seconds * 1000).toISOString().slice(0, 10) : '',
    }));
    download(`${fileStem()}_registrations.csv`,
      [cols.join(',')].concat(rows.map(r => cols.map(c => cell(r[c])).join(','))).join('\r\n'));
  };

  // Google Contacts, to get a league's players into WhatsApp: import this into
  // Google Contacts, then WhatsApp picks them up from the phone's address book.
  // The league goes in Notes and the team in Organization, which are the two
  // fields Google has that can carry them.
  const exportGoogleContacts = () => {
    const rows = regs
      .filter(r => r.status !== 'cancelled')
      .map(r => ({
        firstName: r.firstName,
        lastName: r.lastName,
        email: r.email,
        phone: r.phone,
        notes: league?.name || 'League',
        organization: r.teamName || '',
      }));
    download(`${fileStem()}_contacts.csv`, googleContactsCsv(rows));
  };

  // vb-scores: one row per entrant, "Names,Points" — the same two columns the
  // tournament export writes, so both feed the scoreboard the same way. Points
  // is left blank because that is what vb-scores fills in.
  //
  // Players who are not on a team are entrants too. The junior league is how
  // this showed: juniors sign up individually and are paired later, so every
  // registration had a null teamId, the team grouping matched nothing, and the
  // file came out as a header and no players at all. A player waiting to be
  // paired is still someone vb-scores needs to know about, so they export as
  // their own row — first and last name, since a lone first name is not enough
  // to tell two juniors apart on a scoreboard.
  const exportVbScores = () => {
    const entrants = regs.filter(r => r.status !== 'cancelled' && r.status !== 'waitlisted');
    const byTeam = new Map();
    const solo = [];
    entrants.forEach(r => {
      if (r.teamId) {
        if (!byTeam.has(r.teamId)) byTeam.set(r.teamId, { name: r.teamName || '', players: [] });
        byTeam.get(r.teamId).players.push(String(r.firstName || '').trim());
      } else {
        solo.push(`${String(r.firstName || '').trim()} ${String(r.lastName || '').trim()}`.trim());
      }
    });
    const names = [
      // A team's own name if it has one, otherwise its players' first names —
      // which is what an auto-named pair is called anyway.
      ...[...byTeam.values()].map(t => t.name || t.players.filter(Boolean).join(' / ')),
      ...solo,
    ].filter(Boolean).sort((a, b) => a.localeCompare(b));
    const lines = ['Names,Points'].concat(names.map(n => `${cell(n)},`));
    // vb-scores reads the club off the "torch:" prefix, so the name is part of
    // the format rather than decoration. Browsers on Windows (and Chrome on
    // macOS) rewrite a colon in a download name to an underscore — if the file
    // lands as torch_… it has to be renamed by hand before vb-scores takes it.
    download(`torch:${fileStem()}.csv`, lines.join('\r\n'));
  };

  // "We'll send you a new link" needed a link to send. Mints a fresh checkout
  // for a registration that was never paid and copies it, so it can go out by
  // text or email — and the player pays, rather than an admin marking as paid
  // something nobody has actually paid.
  const copyPayLink = async (r) => {
    setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'createLeaguePaymentSession');
      const { data } = await fn({ registrationId: r.id });
      const ok = data?.url ? await copyText(data.url) : false;
      if (ok) flash(`Payment link copied — $${((data.amountCents || 0) / 100).toFixed(2)} for ${r.firstName || 'this player'}.`);
      else if (data?.url) setError(`Could not copy it. The link is: ${data.url}`);
      else setError('Could not create a payment link.');
    } catch (e) {
      setError(e.message || 'Could not create a payment link.');
    }
  };

  // Refunding does NOT cancel the registration: the row is the record of who
  // entered, and whether they are off the roster is a separate decision. The
  // charge.refunded webhook writes the amounts back when Stripe confirms.
  const [refunding, setRefunding] = useState(false);
  const refundOne = async (r) => {
    const paid = ((Number(r.amountPaid) || 0) - (Number(r.amountRefunded) || 0)) / 100;
    if (!window.confirm(
      `Refund $${paid.toFixed(2)} to ${r.firstName || 'this player'}?\n\n`
      + 'The money goes back to their card and they are emailed to say so. They stay '
      + 'registered — remove them separately if they are not playing.')) return;
    setRefunding(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'refundLeagueRegistration');
      const { data } = await fn({ registrationId: r.id });
      if (data.refunded) {
        flash(`Refunded $${(data.refundedCents / 100).toFixed(2)}.`
          + (data.emailed ? ' They have been emailed.' : ' The email did not send — tell them yourself.'));
      }
      else setError(data.results?.[0]?.skipped || data.results?.[0]?.error || 'Nothing was refunded.');
    } catch (e) {
      setError(e.message || 'The refund did not go through.');
    }
    setRefunding(false);
  };

  // For a cancelled league, where the alternative is the same number of trips
  // through the Stripe dashboard.
  const refundEveryone = async () => {
    const owedBack = regs
      .filter(r => r.status !== 'cancelled')
      .reduce((sum, r) => sum + Math.max(0, (Number(r.amountPaid) || 0) - (Number(r.amountRefunded) || 0)), 0);
    if (!window.confirm(
      `Refund everyone in ${league?.name || 'this league'}?\n\n`
      + `$${(owedBack / 100).toFixed(2)} goes back across ${regs.filter(r => (Number(r.amountPaid) || 0) > 0).length} payment(s), `
      + 'and each player is emailed to say so. Everyone stays registered; this only moves the money.')) return;
    setRefunding(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'refundLeagueRegistration');
      const { data } = await fn({ leagueId });
      flash(`Refunded $${(data.refundedCents / 100).toFixed(2)} to ${data.refunded} player(s), `
        + `${data.emailed} emailed.`
        + (data.skipped ? ` ${data.skipped} had nothing to refund.` : '')
        + (data.failed ? ` ${data.failed} failed — check Stripe.` : ''));
    } catch (e) {
      setError(e.message || 'The refunds did not go through.');
    }
    setRefunding(false);
  };

  const markPaid = async (r) => {
    try {
      await updateDoc(doc(db, 'leagueRegistrations', r.id), {
        paymentStatus: 'paid',
        amountPaid: r.amountPaid || r.priceCents || 0,
        paidAt: serverTimestamp(),
        paidManuallyBy: 'admin',
      });
      flash('Marked paid.');
    } catch (e) { setError('Could not mark paid: ' + e.message); }
  };

  // Promoting off the wait-list only changes their status — it does not take
  // payment. They were never charged, so an admin still has to collect.
  // Someone rings or messages asking about a full league. Two ways to deal
  // with them: send the sign-up link, which wait-lists automatically once the
  // league is full, or put them on the list yourself while they are on the
  // phone. Both end in the same row.
  const signupLink = `${window.location.origin}/league-signup?league=${leagueId}`;

  const copySignupLink = async () => {
    try {
      await navigator.clipboard.writeText(signupLink);
      flash('Sign-up link copied. While the league is full it adds people to the wait-list.');
    } catch (e) {
      // Clipboard access is refused outside a secure context and in some
      // browsers; showing the link is more use than an error.
      setError(`Copy failed — the link is ${signupLink}`);
    }
  };

  const nextWaitPosition = () =>
    regs.reduce((max, r) => r.status === 'waitlisted'
      ? Math.max(max, Number(r.waitlistPosition) || 0) : max, 0) + 1;

  const addToWaitlist = async () => {
    const f = waitForm;
    if (!f.firstName.trim() || !f.lastName.trim()) { setError('A first and last name are needed.'); return; }
    setAddingWait(true); setError('');
    try {
      await addDoc(collection(db, 'leagueRegistrations'), {
        leagueId,
        leagueName: league?.name || '',
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        email: f.email.trim().toLowerCase(),
        phone: f.phone.trim(),
        notes: f.notes.trim(),
        status: 'waitlisted',
        waitlistPosition: nextWaitPosition(),
        // Nothing owed until they are promoted, which is what promote() sets.
        paymentStatus: 'pending',
        amountPaid: 0,
        // Leagues store their price in cents as `price`; there is no priceCents
        // on the league, and reading it made every hand-added row free, so
        // Promote marked them paid.
        priceCents: Number(league?.price) || 0,
        // No account attached: this person reached out directly and may not
        // have one. A phone match is not identity, so userId stays null and
        // the row carries the name the club was given.
        userId: null,
        teamId: null,
        source: 'admin-waitlist',
        addedBy: auth.currentUser?.uid || null,
        createdAt: serverTimestamp(),
      });
      setWaitOpen(false);
      setWaitForm(emptyWaitForm);
      flash(`${f.firstName.trim()} added to the wait-list.`);
    } catch (e) { setError('Could not add them: ' + e.message); }
    setAddingWait(false);
  };

  // The inverse of promote, for a place given up or taken back. Position goes
  // to the end of the queue rather than wherever they were before: the people
  // who waited while they held a place should not be jumped.
  const demote = async (r) => {
    if (!window.confirm(`Move ${r.firstName} ${r.lastName} to the wait-list?`)) return;
    try {
      await updateDoc(doc(db, 'leagueRegistrations', r.id), {
        status: 'waitlisted',
        waitlistPosition: nextWaitPosition(),
      });
      flash((Number(r.amountPaid) || 0) > 0
        ? 'Moved to the wait-list. They have paid — refund separately if that is the intention.'
        : 'Moved to the wait-list.');
    } catch (e) { setError('Could not move them: ' + e.message); }
  };

  const promote = async (r) => {
    try {
      await updateDoc(doc(db, 'leagueRegistrations', r.id), {
        status: 'registered',
        waitlistPosition: null,
        paymentStatus: (r.priceCents || 0) > 0 ? 'pending' : 'paid',
      });
      flash('Promoted off the wait-list. They have not been charged — collect payment separately.');
    } catch (e) { setError('Could not promote: ' + e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.firstName} ${r.lastName} from this league?`)) return;
    try {
      await deleteDoc(doc(db, 'leagueRegistrations', r.id));
      flash('Registration removed.');
    } catch (e) { setError('Could not remove: ' + e.message); }
  };

  const active = regs.filter(r => r.status !== 'waitlisted');
  const waiting = regs.filter(r => r.status === 'waitlisted');
  const paidCount = regs.filter(r => r.paymentStatus === 'paid').length;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/tournament-admin/leagues')}><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h4" fontWeight="bold">{league?.name || 'League'} — Registrations</Typography>
          <Typography variant="body2" color="text.secondary">
            {active.length} registered{Number(league?.maxTeams) > 0
              ? ` of ${Number(league.maxTeams) * (Number(league.rosterMax) || 0)}` : ''}
            {waiting.length ? ` · ${waiting.length} wait-listed` : ''} · {paidCount} paid
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={smsRecipients.length
          ? `Opens your own messaging app with ${smsRecipients.length} recipient(s) — the replies come back to you`
          : 'Nobody on this list has a usable mobile number'}>
          <span>
            <Button size="small" variant="outlined" startIcon={<SmsIcon />}
              disabled={smsRecipients.length === 0} href={smsHref}>
              Text everyone
            </Button>
          </span>
        </Tooltip>
        {/* Not every desktop has a messaging app the sms: link can reach, so
            the numbers are always available to paste somewhere else. */}
        <Tooltip title="Copy the public sign-up link. While the league is full it puts people on the wait-list.">
          <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={copySignupLink}>
            Sign-up link
          </Button>
        </Tooltip>

        <Tooltip title="Put someone on the wait-list yourself — for whoever reached out directly">
          <Button size="small" variant="outlined" startIcon={<PersonAddAlt1Icon />}
            onClick={() => { setWaitForm(emptyWaitForm); setWaitOpen(true); }}>
            Add to wait-list
          </Button>
        </Tooltip>

        <Tooltip title="Send a text from the club's number to everyone entered">
          <span>
            <Button size="small" variant="outlined" startIcon={<SmsIcon />}
              disabled={regs.length === 0} onClick={() => setSmsOpen(true)} sx={{ ml: 1 }}>
              Send text
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Copy the numbers">
          <span>
            <IconButton size="small" onClick={copyNumbers}
              disabled={smsRecipients.length === 0} sx={{ ml: 0.5, mr: 1 }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Every field on every registration, for the club's records">
          <span>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
              disabled={regs.length === 0} onClick={exportRegistrations}>
              Export CSV
            </Button>
          </span>
        </Tooltip>
        {/* Only for a league that has been called off: refunding everyone in a
            league that is still running is not a thing anyone means to do. */}
        {league?.status === 'canceled' && regs.some(r => (Number(r.amountPaid) || 0) > (Number(r.amountRefunded) || 0)) && (
          <Tooltip title="Refund every payment in this cancelled league">
            <span>
              <Button size="small" variant="outlined" color="warning" disabled={refunding}
                startIcon={<CurrencyExchangeIcon />} onClick={refundEveryone} sx={{ ml: 1 }}>
                Refund everyone
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Google Contacts format — import to Contacts, then into WhatsApp">
          <span>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
              disabled={regs.length === 0} onClick={exportGoogleContacts} sx={{ ml: 1 }}>
              Contacts
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="One row per team, Names/Points — the format vb-scores reads">
          <span>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
              disabled={regs.length === 0} onClick={exportVbScores} sx={{ ml: 1 }}>
              vb-scores
            </Button>
          </span>
        </Tooltip>
      </Box>

      <EventSmsDialog open={smsOpen} onClose={() => setSmsOpen(false)}
        kind="league" eventId={leagueId} eventName={league?.name || 'League'} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : regs.length === 0 ? (
        <Paper elevation={1} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
          <Typography color="text.secondary">Nobody has registered yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Player</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Payment</strong></TableCell>
                <TableCell align="right"><strong>Paid</strong></TableCell>
                <TableCell><strong>Team</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regs.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {r.firstName} {r.lastName}
                      {ageOn(r.birthdate) != null && (
                        <Typography component="span" variant="caption" color="text.secondary"
                          sx={{ ml: 0.75, fontWeight: 400 }}>
                          age {ageOn(r.birthdate)}
                        </Typography>
                      )}
                    </Typography>
                    {r.guardianName && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Guardian: {r.guardianName}
                      </Typography>
                    )}
                    {/* A wait-listed player's team is not created until they are
                        promoted, so the name they asked for lives here until
                        someone acts on it. */}
                    {r.requestedTeamName && !r.teamId && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        Wants to start "{r.requestedTeamName}"
                      </Typography>
                    )}
                    {r.notes && (
                      <Typography variant="caption" color="text.secondary">{r.notes}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.email}</Typography>
                    {r.phone && <Typography variant="caption" color="text.secondary">{r.phone}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status === 'waitlisted'
                      ? `wait #${r.waitlistPosition ?? '?'}` : (r.status || 'registered')}
                      color={r.status === 'waitlisted' ? 'warning' : 'success'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.paymentStatus || 'pending'}
                      color={PAY_COLOR[r.paymentStatus] || 'default'} />
                  </TableCell>
                  <TableCell align="right">${((Number(r.amountPaid) || 0) / 100).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.teamId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {r.status === 'waitlisted' && (
                      <Tooltip title="Promote off the wait-list">
                        <IconButton size="small" onClick={() => promote(r)}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.status !== 'waitlisted' && r.status !== 'cancelled' && (
                      <Tooltip title="Move to the wait-list — they go to the end of the queue">
                        <IconButton size="small" onClick={() => demote(r)}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(Number(r.amountPaid) || 0) > (Number(r.amountRefunded) || 0) && (
                      <Tooltip title="Refund what they paid — they stay registered">
                        <IconButton size="small" color="warning" disabled={refunding}
                          onClick={() => refundOne(r)}>
                          <CurrencyExchangeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.paymentStatus !== 'paid' && r.status !== 'waitlisted' && (
                      <Tooltip title="Copy a payment link to send them">
                        <IconButton size="small" color="primary" onClick={() => copyPayLink(r)}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.paymentStatus !== 'paid' && r.status !== 'waitlisted' && (
                      <Tooltip title="Mark as paid">
                        <IconButton size="small" color="success" onClick={() => markPaid(r)}>
                          <PaidIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.pendingTeamId && !r.teamId && (
                      <Tooltip title={`Accept the invitation from ${r.pendingTeamName || 'that team'} on their behalf`}>
                        <IconButton size="small" color="primary" onClick={() => acceptFor(r)}>
                          <HowToRegIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => remove(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Adding someone by hand. Only a name is required: whoever reached out
          may have given a number and nothing else, and a row with a name is
          worth more than no row. */}
      <Dialog open={waitOpen} onClose={() => setWaitOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add to the wait-list</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            They go to the end of the queue and owe nothing yet. Promoting them later
            sets the fee — collect it then.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="First name" size="small" fullWidth autoFocus
                value={waitForm.firstName}
                onChange={e => setWaitForm(f => ({ ...f, firstName: e.target.value }))} />
              <TextField label="Last name" size="small" fullWidth
                value={waitForm.lastName}
                onChange={e => setWaitForm(f => ({ ...f, lastName: e.target.value }))} />
            </Box>
            <TextField label="Phone" size="small" fullWidth value={waitForm.phone}
              onChange={e => setWaitForm(f => ({ ...f, phone: e.target.value }))} />
            <TextField label="Email" size="small" fullWidth value={waitForm.email}
              onChange={e => setWaitForm(f => ({ ...f, email: e.target.value }))} />
            <TextField label="Note" size="small" fullWidth multiline minRows={2}
              placeholder="How they got in touch, who they want to play with…"
              value={waitForm.notes}
              onChange={e => setWaitForm(f => ({ ...f, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaitOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={addingWait} onClick={addToWaitlist}>
            {addingWait ? 'Adding…' : 'Add to wait-list'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
